<template>
  <settings-checker>
    <page-loader v-if="loadingCheckoutSession"/>
    <div class="booking" v-else>
      <div class="booking-wrapper">
        <petstar-ribbon/>
        <div class="booking-divider booking-divider-left">
          <div class="booking-content">
            <booking-header/>
            <div class="w-100 d-block d-md-none px-4 mb-3">
              <home-button v-if="isOnlineBooking"/>
            </div>
            <opening-hours/>
          </div>
          <div class="fixed-buttons d-none d-md-block">
            <home-button v-if="isOnlineBooking"/>
          </div>
        </div>
        <div class="booking-divider booking-divider-right">
          <div class="booking-content">
            <display-banner/>
            <div class="d-flex flex-column justify-content-center align-items-center h-100 mt-5 px-4">
              <error-banner v-if="updateError" :message="updateError"/>
              <div class="font-size-h3 text-center" :class="closed ? 'text-default' : 'text-danger'">
                <span><b>{{ paymentLabel }}</b> <template v-if="closed">{{ appointmentLabel }} for</template></span>
                <span v-if="closed" class="font-weight-bold">{{ formattedStartDay }}, {{ formattedStartDate }} at {{ formattedStartTime }}</span>
                <span>{{ bookingStatusMessage }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </settings-checker>
</template>

<script>
import moment from 'moment-timezone'
import { mapActions, mapState } from 'vuex'
import { ErrorHelper } from '@/helpers/ErrorHelper'
import { DateFormatMixin } from '@/mixins/Booking/DateFormatMixin'
import { TimeFormatMixin } from '@/mixins/Booking/TimeFormatMixin'
import DisplayBanner from '@/components/Booking/DisplayBanner.vue'
import PetstarRibbon from '@/components/Booking/PetstarRibbon.vue'
import Header from '@/components/Booking/Header.vue'
import SettingsChecker from './SettingsChecker.vue'
import HomeButton from '@/components/Booking/HomeButton.vue'
import OpeningHours from '@/components/Booking/OpeningHours.vue'
import ErrorBanner from '@/components/ErrorValidation/ErrorBanner.vue'
import PageLoader from '../Loading/PageLoader.vue'

export default {
  name: 'CheckoutSessionPage',
  mixins: [DateFormatMixin, TimeFormatMixin],
  data () {
    return {
      updateError: ''
    }
  },
  computed: {
    ...mapState('CheckoutSession', [
      'checkoutSession',
      'loadingCheckoutSession'
    ]),
    formattedStartUtc () {
      if (this.startUtc) {
        return moment.utc(this.startUtc).clone().tz(this.timeZoneId)
      }
      return null
    },
    formattedStartDay () {
      return this.formattedStartUtc ? this.formattedStartUtc.format('dddd') : ''
    },
    formattedStartDate () {
      return this.formattedStartUtc ? this.formattedStartUtc.format(this.momentJSDateFormat) : ''
    },
    formattedStartTime () {
      return this.formattedStartUtc ? this.formattedStartUtc.format(this.momentJSTimeFormat) : ''
    },
    token () {
      return this.$route.query.token
    },
    checkoutId () {
      const raw = this.$route.query.checkoutId || this.$route.query.checkoutid
      if (typeof raw !== 'string') return null
      const [cleanId] = raw.split('?')
      return decodeURIComponent(cleanId)
    },
    timeZoneId () {
      return this.checkoutSession?.timeZoneId || 'Europe/London'
    },
    startUtc () {
      return this.checkoutSession?.startUtc || null
    },
    status () {
      return this.checkoutSession?.status || null
    },
    isOnlineBooking () {
      return this.checkoutSession?.isOnlineBooking
    },
    appointmentLabel () {
      return this.isOnlineBooking ? 'Your booking' : 'Your appointment'
    },
    paymentLabel () {
      return this.closed ? 'Payment received.' : 'Payment failed.'
    },
    bookingStatus () {
      return this.checkoutSession?.bookingStatus || null
    },
    bookingStatusMessage () {
      if (!this.isOnlineBooking) return this.closed ? 'is confirmed.' : ''
      if (this.closed) {
        switch (this.bookingStatus?.id) {
          case 1: return 'is under review. An email will be sent shortly.'
          case 2: return 'is confirmed. A confirmation email is on the way.'
          case 3: return 'was declined.'
          case 4: return 'has expired.'
          default: return ''
        }
      }
      return this.bookingStatus?.id === 1
        ? "Your booking will be reviewed. We'll email you if approved."
        : ''
    },
    closed () {
      return this.status && this.status.id > 4
    }
  },
  watch: {
    token () {
      this.refreshCheckoutSession()
    }
  },
  methods: {
    ...mapActions('CheckoutSession', [
      'updateCheckoutSession'
    ]),
    refreshCheckoutSession () {
      if (!this.checkoutSession) {
        this.tryUpdateCheckoutSession()
      }
    },
    tryUpdateCheckoutSession () {
      const self = this
      self.updateError = ''
      if (!self.token || !self.checkoutId || !!self.loadingCheckoutSession) {
        return
      }
      self.updateCheckoutSession({ token: self.token, checkoutId: self.checkoutId })
        .catch(error => { self.updateError = ErrorHelper.getError(error) })
    }
  },
  created () {
    this.refreshCheckoutSession()
  },
  components: {
    'settings-checker': SettingsChecker,
    'petstar-ribbon': PetstarRibbon,
    'booking-header': Header,
    'home-button': HomeButton,
    'opening-hours': OpeningHours,
    'display-banner': DisplayBanner,
    'error-banner': ErrorBanner,
    'page-loader': PageLoader
  }
}
</script>

<style>
  .booking-payment-processor .booking-divider-right.booking-payment-widget .booking-content {
    max-height: calc(100% - 8.25rem) !important
  }
  .booking-payment-processor .booking-divider-right.booking-payment-widget .fixed-buttons {
    height: 8.25rem !important
  }
</style>
